"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const reselect_1 = require("reselect");
const get_1 = __importDefault(require("lodash/get"));
const nucleus_1 = require("@lifesize/nucleus");
const constants_1 = require("../constants");
const { canControlFarEndCamera } = nucleus_1.activeCallSelectors;
const { activeCallSelector, incomingCallsSelector } = nucleus_1.callSelectors;
const { searchContacts } = nucleus_1.searchSelectors;
const { contactsSelector, isActiveMeeting, meetingsFilter, roomSystemsAndMeetingsFilter, usersAndRoomSystemsFilter, usersAndContactsFilter, availableUsersAndRoomSystemsFilter, favoritesSelector, getContactByUuidGenerator, selectPagedLoads, selectErrorFetching } = nucleus_1.contactSelectors;
const { callHistorySelector } = nucleus_1.callHistorySelectors;
const { myInfo, getClientType } = nucleus_1.currentUserSelectors;
const { getFeatureFlags } = nucleus_1.featureSelectors;
const { getEndpointSoftwareVersions, getEndpointLicenseStatus } = nucleus_1.endpointSelectors;
const { selectCanCurrentUserKickParticipants, selectIsLocked, selectConferenceState, selectConferenceType, selectCanIssueHandCommand } = nucleus_1.conferenceSelectors;
const { selectParticipantList, selectParticipantsWithRaisedHands, selectIsCurrentUserHandRaised } = nucleus_1.conferenceParticipantsSelectors;
const { selectRemoteModeratorList } = nucleus_1.conferenceRemoteModeratorsSelectors;
const { getAccountFeatures } = nucleus_1.accountSelectors;
const { selectCanLowerAllHands } = nucleus_1.moderatorSelectors;
const { selectConnected } = nucleus_1.servicesSelectors;
const remoteCallHistorySelector = state => get_1.default(state, 'app.remoting.controller.callHistory', []);
const remoteContactsSelector = state => get_1.default(state, 'app.remoting.controller.contacts', []);
const remoteSearchContactsSelector = state => {
    const searchContacts = get_1.default(state, 'app.remoting.controller.searchContacts', []);
    const participants = get_1.default(state, 'app.remoting.controller.activeCall.participants') || [];
    const updatedContacts = searchContacts.map(c => {
        return Object.assign(Object.assign({}, c), { isCallable: participants.every(p => p.dialString !== c.dialString) && c.isOnline });
    });
    return updatedContacts;
};
const remoteCalendarSelector = state => get_1.default(state, 'app.remoting.controller.calendar', null);
const remoteFavoritesSelector = state => get_1.default(state, 'app.remoting.controller.favorites', []);
const remoteDoNotDisturbSelector = state => get_1.default(state, 'app.remoting.controller.doNotDisturb', false);
const displaySelector = state => {
    const displayState = get_1.default(state, 'app.remoting.display', {});
    const showInCallLayout = get_1.default(state, 'nucleus.device.screenCount') !== 2;
    return Object.assign(Object.assign({}, displayState), { showInCallLayout });
};
const getWirelessPresentationUrl = state => {
    const clusterUrl = get_1.default(state, 'nucleus.cluster.url');
    if (clusterUrl === nucleus_1.nucleusConstants.PROD_CLUSTER_URL)
        return 'https://present.lifesize.com';
    return `https://present.${clusterUrl}`;
};
const devicePinSelector = state => get_1.default(state, 'app.remoting.pairing.devicePin', '');
const presentationCodeSelector = state => get_1.default(state, 'app.remoting.pairing.remoteLinkingCode', '');
const presentationUrlSelector = state => getWirelessPresentationUrl(state);
const isReceivingGalaxyPresentation = state => {
    const presentationState = get_1.default(state, 'nucleus.galaxy.presentation.state');
    return (presentationState === nucleus_1.nucleusConstants.GALAXY_PRESENTATION_STATE_RX_STARTING ||
        presentationState === nucleus_1.nucleusConstants.GALAXY_PRESENTATION_STATE_RX_STARTED ||
        presentationState === nucleus_1.nucleusConstants.GALAXY_PRESENTATION_STATE_RX_STOPPING);
};
const getActiveWirelessPresenter = state => get_1.default(state, 'app.remoting.pairing.activePresenter');
const getCanPresent = state => {
    const galaxyRemoteIsPresenting = isReceivingGalaxyPresentation(state);
    const activeCall = activeCallSelector(state);
    const callIsActive = activeCall.state === nucleus_1.nucleusConstants.CALL_STATE_CONNECTED;
    const isLicenseExpired = getEndpointLicenseStatus(state) === constants_1.pairing.licenseStatus.inactive;
    return !(callIsActive && galaxyRemoteIsPresenting) && !isLicenseExpired;
};
const getDisplayIsAvailable = state => {
    const isServicesConnected = selectConnected(state);
    const isLinkedToRemote = !!get_1.default(state, 'app.remoting.pairing.pairedUuid');
    return isServicesConnected && isLinkedToRemote;
};
const getIsReceivingWirelessPresentation = state => get_1.default(state, 'app.remoting.wirelessPresentation.receiving', false);
const getRoomName = state => myInfo(state).name;
const getPlatform = state => get_1.default(state, 'app.remoting.pairing.displayPlatform');
const getCalendar = state => get_1.default(state, 'app.remoting.calendar');
const getDoNotDisturb = state => get_1.default(state, 'nucleus.doNotDisturb');
const getDeviceCameraCapabilities = state => get_1.default(state, 'nucleus.device.cameraCapabilities');
const getHdmiPresentation = state => get_1.default(state, 'app.remoting.hdmiPresentation');
const getSharePresentation = state => get_1.default(state, 'app.remoting.sharePresentation');
const getOwnerUUID = state => get_1.default(state, 'nucleus.galaxy.recording.owner.uuid');
const getAppDirectory = state => get_1.default(state, 'app.directory');
const getSystemInfo = state => {
    return {
        serialNumber: get_1.default(state, 'nucleus.device.serialNumber'),
        ipAddress: get_1.default(state, 'nucleus.device.ipAddress'),
        version: get_1.default(state, 'nucleus.userProfile.clientInfo.version'),
        usageType: get_1.default(state, 'nucleus.endpoint.usageType'),
        isSipRegistered: get_1.default(state, 'app.sip.isRegistered')
    };
};
const buildRemoteContactSelector = contact => {
    const { id, uuid, name, contactType, dialString, isMeeting, isOnline, isPinProtected, isRoomSystem, participantsCount, isCallable, isUser, isPersonalContact, isGroupContact, isFavoritable, favorite } = contact;
    return {
        contactType,
        dialString,
        id,
        uuid,
        isCallable,
        isMeeting,
        isOnline,
        isPinProtected,
        isRoomSystem,
        isUser,
        name,
        participantsCount,
        isPersonalContact,
        isGroupContact,
        isFavoritable,
        favorite
    };
};
const roomSystemsAndMeetingsSelector = reselect_1.createSelector(remoteContactsSelector, contacts => contacts.filter(roomSystemsAndMeetingsFilter));
const activeMeetingsSelector = reselect_1.createSelector(remoteContactsSelector, contacts => contacts.filter(isActiveMeeting));
const meetingsSelector = reselect_1.createSelector(remoteContactsSelector, contacts => contacts.filter(meetingsFilter));
const roomSystemsSelector = reselect_1.createSelector(remoteContactsSelector, contacts => contacts.filter(c => c.isRoomSystem));
const usersSelector = reselect_1.createSelector(remoteContactsSelector, contacts => contacts.filter(c => c.isUser));
const usersAndRoomSystemsSelector = reselect_1.createSelector(remoteContactsSelector, contacts => contacts.filter(usersAndRoomSystemsFilter));
const usersAndContactsSelector = reselect_1.createSelector(remoteContactsSelector, contacts => contacts.filter(usersAndContactsFilter));
const remoteSearchAvailableUsersAndRoomSystems = reselect_1.createSelector(remoteSearchContactsSelector, contacts => contacts.filter(availableUsersAndRoomSystemsFilter));
const remoteSearchUsers = reselect_1.createSelector(remoteSearchContactsSelector, contacts => contacts.filter(c => c.isUser));
const buildRemoteContactsSelector = reselect_1.createSelector(contactsSelector, contacts => contacts.map(c => buildRemoteContactSelector(c)));
const buildRemotePagedContactsSelector = reselect_1.createSelector([selectPagedLoads, selectErrorFetching], (pagedLoads, errorFetching) => {
    return {
        pagedLoads,
        errorFetching
    };
});
const buildSearchContacts = reselect_1.createSelector(searchContacts, matches => matches.map(m => buildRemoteContactSelector(m.contact)));
const incomingCallSelector = reselect_1.createSelector([incomingCallsSelector], incomingCalls => incomingCalls.length ? incomingCalls[0] : null);
// Updates the call history contact when the matching contact is updated
const updatedCallHistorySelector = state => {
    const callHistory = callHistorySelector(state);
    const getContactById = getContactByUuidGenerator(state);
    if (callHistory.length > 0) {
        // Map the call history entry to the contact
        callHistory.forEach(c => {
            c.contact = getContactById(get_1.default(c, 'contact.uuid'));
        });
    }
    return callHistory;
};
// Composes the active call state with any additional data we might want to add to it
const buildActiveCallSelector = state => {
    const activeCall = activeCallSelector(state);
    const getOwner = getContactByUuidGenerator(state);
    return Object.assign(Object.assign({}, activeCall), { participants: selectParticipantList(state), canKickParticipants: selectCanCurrentUserKickParticipants(state), isMeetingLocked: selectIsLocked(state), conferenceState: selectConferenceState(state), conferenceType: selectConferenceType(state), recordingOwner: getOwner(getOwnerUUID(state)), canLowerAllHands: selectCanLowerAllHands(state), participantsWithRaisedHands: selectParticipantsWithRaisedHands(state), isHandRaised: selectIsCurrentUserHandRaised(state), canIssueHandCommand: selectCanIssueHandCommand(state) });
};
/**
 * Returns a subset of the state relevant to the remote
 */
const remoteStateSelector = reselect_1.createSelector([
    incomingCallSelector,
    buildActiveCallSelector,
    myInfo,
    buildRemoteContactsSelector,
    buildSearchContacts,
    buildRemotePagedContactsSelector,
    displaySelector,
    devicePinSelector,
    getDoNotDisturb,
    updatedCallHistorySelector,
    getIsReceivingWirelessPresentation,
    presentationCodeSelector,
    presentationUrlSelector,
    getEndpointSoftwareVersions,
    getEndpointLicenseStatus,
    getFeatureFlags,
    getPlatform,
    canControlFarEndCamera,
    getCalendar,
    favoritesSelector,
    getHdmiPresentation,
    getSharePresentation,
    getDeviceCameraCapabilities,
    getClientType,
    getSystemInfo,
    selectRemoteModeratorList,
    getAccountFeatures,
    getAppDirectory
], (incomingCall, activeCall, userInfo, contacts, searchContacts, pagedContacts, display, devicePin, doNotDisturb, callHistory, isReceivingWirelessPresentation, presentationCode, presentationUrl, software, licenseStatus, features, platform, canControlFarEndCamera, calendar, favorites, hdmiPresentation, sharePresentation, deviceCameraCapabilities, linkedClientType, systemInfo, remoteModerators, accountFeatures, directory) => {
    return {
        activeCall,
        display,
        callHistory,
        canControlFarEndCamera,
        contacts,
        devicePin,
        doNotDisturb,
        deviceCameraCapabilities,
        features,
        incomingCall,
        isReceivingWirelessPresentation,
        presentationCode,
        presentationUrl,
        searchContacts,
        software,
        licenseStatus,
        userInfo,
        platform,
        calendar,
        favorites,
        hdmiPresentation,
        sharePresentation,
        linkedClientType,
        systemInfo,
        remoteModerators,
        accountFeatures,
        pagedContacts,
        directory
    };
});
/**
 * Returns a subset of the state relevant to the wireless presenters
 */
const wirelessPresenterStateSelector = reselect_1.createSelector([getActiveWirelessPresenter, getCanPresent, getDisplayIsAvailable, getIsReceivingWirelessPresentation, getRoomName], (activeWirelessPresenter, canPresent, displayIsAvailable, wirelessPresentationIsActive, roomName) => {
    return {
        activeWirelessPresenter,
        canPresent,
        displayIsAvailable,
        roomName,
        wirelessPresentationIsActive
    };
});
exports.default = {
    activeMeetingsSelector,
    meetingsSelector,
    remoteCallHistorySelector,
    remoteContactsSelector,
    remoteCalendarSelector,
    remoteDoNotDisturbSelector,
    remoteFavoritesSelector,
    remoteSearchAvailableUsersAndRoomSystems,
    remoteSearchUsers,
    remoteSearchContactsSelector,
    remoteStateSelector,
    roomSystemsSelector,
    roomSystemsAndMeetingsSelector,
    usersAndRoomSystemsSelector,
    usersAndContactsSelector,
    usersSelector,
    wirelessPresenterStateSelector,
    getWirelessPresentationUrl
};



//////////////////
// WEBPACK FOOTER
// ./node_modules/@lifesize/clients.remoting/dist/selectors/remoting.js
// module id = 159
// module chunks = 0